/**
 * Recharge widget suppressor.
 *
 * The Recharge "Subscribe & save / One-time purchase" widget is hidden via
 * recharge-widget-suppress.css. This script handles the form-side concern:
 * the widget injects a hidden <select name="selling_plan"> into the product
 * form and pre-selects the "subsave" radio, which would otherwise force the
 * cart-add to use a Recharge subscription plan and break the theme's
 * custom .purchase-options selector.
 *
 * Strategy:
 *   1. As soon as the widget appears in the DOM, click its "One-time
 *      purchase" radio so its internal Vue state is consistent.
 *   2. Disable / detach the Recharge selling_plan <select> from the form
 *      so it cannot submit a value. The theme's own .purchase-options
 *      <select name="selling_plan"> remains the sole source of truth.
 *   3. Re-run on every Vue re-render via a MutationObserver so changes
 *      from variant swaps or Recharge SDK updates don't regress the state.
 */

(() => {
  // Idempotency guard — if multiple copies of this script load, only one wires up.
  if (window.__rechargeWidgetSuppressorInstalled) return;
  window.__rechargeWidgetSuppressorInstalled = true;

  const RECHARGE_ROOT_SELECTOR = '.rc-container-wrapper, .rc_container_wrapper, [data-widget-container-wrapper]';
  const ONETIME_RADIO_SELECTOR = 'input[data-radio-onetime], input[value="onetime"]';
  const RECHARGE_PLAN_SELECT_SELECTOR = 'select[data-plans-dropdown], select[name="selling_plan"][data-v-335eb9f1]';

  /**
   * Find every Recharge widget root currently in the document and
   * neutralise it.
   */
  function neutraliseRechargeWidgets(root = document) {
    const widgets = root.querySelectorAll(RECHARGE_ROOT_SELECTOR);
    widgets.forEach(neutraliseWidget);
  }

  /**
   * Neutralise one Recharge widget instance.
   * @param {Element} widget
   */
  function neutraliseWidget(widget) {
    // 1. Click the "One-time purchase" radio so the widget's own Vue
    //    state is internally consistent (matches what we're submitting).
    const onetimeRadio = widget.querySelector(ONETIME_RADIO_SELECTOR);
    if (onetimeRadio instanceof HTMLInputElement && !onetimeRadio.checked) {
      // Use .click() rather than setting .checked so Vue sees the change
      // event and updates its internal model. If Vue is missing or the
      // click fails silently, fall back to setting .checked + dispatching.
      try {
        onetimeRadio.click();
      } catch (_) {
        onetimeRadio.checked = true;
        onetimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 2. Detach the Recharge selling_plan <select> from the form so it
    //    cannot submit a selling_plan value. We rename the `name`
    //    attribute and disable the element.
    //
    //    Using `disabled` alone would work, but the widget's Vue
    //    instance may re-enable it on re-render, so renaming is safer.
    const planSelect = widget.querySelector(RECHARGE_PLAN_SELECT_SELECTOR);
    if (planSelect instanceof HTMLSelectElement) {
      if (planSelect.name === 'selling_plan') {
        planSelect.name = 'recharge_selling_plan_suppressed';
      }
      planSelect.disabled = true;
    }
  }

  /**
   * Watch for Recharge widgets being inserted or rebuilt by Recharge's Vue runtime, and
   * re-neutralise them.
   *
   * PERF-07 PDP-12: the document itself is watched for inserted nodes only, and only an
   * inserted node that is or holds a widget triggers a sweep. Attribute changes are watched
   * inside each widget, not across the page: every class change anywhere (reveal
   * animations, sliders, apps) used to walk up the DOM, and every insertion anywhere
   * re-queried the whole document. Sweeps are coalesced to one per frame.
   */
  function watchForRechargeWidgets() {
    /** @type {WeakSet<Element>} */
    const watched = new WeakSet();
    let scheduled = false;

    const widgetObserver = new MutationObserver(() => schedule());

    const sweep = () => {
      scheduled = false;
      document.querySelectorAll(RECHARGE_ROOT_SELECTOR).forEach((widget) => {
        neutraliseWidget(widget);
        if (watched.has(widget)) return;
        watched.add(widget);
        widgetObserver.observe(widget, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'data-widget', 'data-template'],
        });
      });
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(sweep);
    };

    const holdsWidget = (/** @type {Node} */ node) =>
      node instanceof Element && (node.matches(RECHARGE_ROOT_SELECTOR) || node.querySelector(RECHARGE_ROOT_SELECTOR) !== null);

    // Initial sweep covers the case where the widget is already in the DOM.
    sweep();

    new MutationObserver((mutations) => {
      if (scheduled) return;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (holdsWidget(node)) {
            schedule();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForRechargeWidgets, { once: true });
  } else {
    watchForRechargeWidgets();
  }
})();
